import { AbsgArrayHelper } from '@absg/angular-common';
import { DateFormat, DateTimeHelper } from '@absg/angular-platform';
import { AssignedProductDetail } from '@absg/api-formulary';
import { InventoryItemProduct, InventoryItemSummary } from '@absg/api-inventory';
import { CalculationHelper, ManagePosition, RuleContainerSetting, Rules, StringConstants, UnassignBin } from '@absg/nim-client-shared';
import { RuleContainerSettingGenerator } from 'shared.root/generator/rule-container-setting.generator';
import { InventoryLotHelper } from 'shared.root/helpers/inventory-lot-helper';
import { InventoryItemProductLot } from 'shared.root/models/inventory-item-product-lot.model';

export class ManagePositionHelper {
  static readonly formattedMarkerDate = DateTimeHelper.formatDate(new Date('9999-12-31T00:00:00'));
  static getLastAuditDateString(managePosition: ManagePosition, defaultValue = StringConstants.neverAudited): string {
    if (!managePosition) { return defaultValue; }
    if (!AbsgArrayHelper.isNil(managePosition.inventoryItems) && managePosition.inventoryItems.length > 1) {
      const temp = AbsgArrayHelper.filter(managePosition.inventoryItems, (obj) => !AbsgArrayHelper.isNil(obj.lastAuditDate)
        && !DateTimeHelper.isMinDate(obj.lastAuditDate));
      if (temp && temp.length > 0) {
        const sorted = AbsgArrayHelper.sortBy(temp, o => o.lastAuditDate).reverse();
        const displayDate = DateTimeHelper.convertUTCToLocalJSDate(sorted[0].lastAuditDate);
        return DateTimeHelper.formatDateTime(displayDate);
      } else {
        return defaultValue;
      }
    }

    if (!managePosition.inventoryItem
      || !managePosition.inventoryItem.lastAuditDate
      || DateTimeHelper.isMinDate(managePosition.inventoryItem.lastAuditDate)) {
      return defaultValue;
    } else {
      const displayDate = DateTimeHelper.convertUTCToLocalJSDate(managePosition.inventoryItem.lastAuditDate);
      return DateTimeHelper.formatDateTime(displayDate);
    }
  }

  static getLastDispenseDateString(managePosition: ManagePosition, defaultValue = ''): string {
    if (!managePosition) { return defaultValue; }
    if (!AbsgArrayHelper.isNil(managePosition.inventoryItems) && managePosition.inventoryItems.length > 1) {
      const temp = AbsgArrayHelper.filter(managePosition.inventoryItems, (obj) => !AbsgArrayHelper.isNil(obj.lastDispenseDate)
        && !DateTimeHelper.isMinDate(obj.lastDispenseDate));
      if (temp && temp.length > 0) {
        const sorted = AbsgArrayHelper.sortBy(temp, o => o.lastDispenseDate).reverse();
        const displayDate = DateTimeHelper.convertUTCToLocalJSDate(sorted[0].lastDispenseDate);
        return DateTimeHelper.formatDateTime(displayDate);
      } else {
        return defaultValue;
      }
    }

    if (!managePosition.inventoryItem
      || !managePosition.inventoryItem.lastDispenseDate
      || DateTimeHelper.isMinDate(managePosition.inventoryItem.lastDispenseDate)) {
      return defaultValue;
    } else {
      const displayDate = DateTimeHelper.convertUTCToLocalJSDate(managePosition.inventoryItem.lastDispenseDate);
      return DateTimeHelper.formatDateTime(displayDate);
    }
  }

  static getLastRestockDateTimeString(managePosition: ManagePosition, defaultValue = ''): string {
    if (!managePosition) { return defaultValue; }
    if (!AbsgArrayHelper.isNil(managePosition.inventoryItems) && managePosition.inventoryItems.length > 1) {
      const temp = AbsgArrayHelper.filter(managePosition.inventoryItems, (obj) => !AbsgArrayHelper.isNil(obj.lastRestockDate)
        && !DateTimeHelper.isMinDate(obj.lastRestockDate));
      if (temp && temp.length > 0) {
        const sorted = AbsgArrayHelper.sortBy(temp, o => o.lastRestockDate).reverse();
        const displayDate = DateTimeHelper.convertUTCToLocalJSDate(sorted[0].lastRestockDate);
        return DateTimeHelper.formatDateTime(displayDate);
      } else {
        return defaultValue;
      }
    }

    if (!managePosition.inventoryItem
      || !managePosition.inventoryItem.lastRestockDate
      || DateTimeHelper.isMinDate(managePosition.inventoryItem.lastRestockDate)) {
      return defaultValue;
    } else {
      const displayDate = DateTimeHelper.convertUTCToLocalJSDate(managePosition.inventoryItem.lastRestockDate);
      return DateTimeHelper.formatDateTime(displayDate);
    }
  }

  static getLastRestockDateString(managePosition: ManagePosition, defaultValue = ''): string {
    if (!managePosition) { return defaultValue; }
    if (!managePosition.inventoryItem
      || !managePosition.inventoryItem.lastRestockDate
      || DateTimeHelper.isMinDate(managePosition.inventoryItem.lastRestockDate)) {
      return defaultValue;
    } else {
      const displayDate = DateTimeHelper.convertUTCToLocalJSDate(managePosition.inventoryItem.lastRestockDate);
      return DateTimeHelper.formatDateTime(displayDate, DateTimeHelper.defaultDateFormat);
    }
  }

  static getAssignedDateTimeString(managePosition: ManagePosition, defaultValue = ''): string {
    if (!managePosition) { return defaultValue; }
    if (!managePosition.inventoryItem
      || !managePosition.inventoryItem.modifiedTime
      || DateTimeHelper.isMinDate(managePosition.inventoryItem.modifiedTime)) {
      return defaultValue;
    } else {
      const displayDate = DateTimeHelper.convertUTCToLocalJSDate(managePosition.inventoryItem.modifiedTime);
      return DateTimeHelper.formatDateTime(displayDate);
    }
  }

  static getLastRestockTimeString(managePosition: ManagePosition, defaultValue = ''): string {
    if (!managePosition) { return defaultValue; }
    if (!managePosition.inventoryItem
      || !managePosition.inventoryItem.lastRestockDate
      || DateTimeHelper.isMinDate(managePosition.inventoryItem.lastRestockDate)) {
      return defaultValue;
    } else {
      const displayDate = DateTimeHelper.convertUTCToLocalJSDate(managePosition.inventoryItem.lastRestockDate);
      return DateTimeHelper.formatTime(displayDate);
    }
  }

  static getCurrentQuantityInStation(managePosition: ManagePosition, overrideBinLogic = false): number {
    let quantity = 0;
    if (managePosition) {
      if (managePosition.isBin && !overrideBinLogic) {
        for (const inventoryItem of managePosition.inventoryItems) {
          if (inventoryItem && inventoryItem.inventoryItemProducts) {
            for (const iip of inventoryItem.inventoryItemProducts) {
              quantity += iip.lotQuantity;
            }
          }
        }
      } else {
        if (managePosition.inventoryItem && managePosition.inventoryItem.inventoryItemProducts) {
          for (const iip of managePosition.inventoryItem.inventoryItemProducts) {
            quantity += iip.lotQuantity;
          }
        }
      }
    }
    return quantity;
  }

  static getCurrentQuantityInUnderhood(managePosition: ManagePosition, overrideBinLogic = false): number {
    let quantity = 0;
    if (managePosition) {
      if (managePosition.isBin && !overrideBinLogic) {
        for (const inventoryItem of managePosition.inventoryItems) {
          if (inventoryItem && inventoryItem.inventoryItemProducts) {
            for (const iip of inventoryItem.inventoryItemProducts) {
              quantity += iip.lotQuantityUnderhood;
            }
          }
        }
      } else {
        if (managePosition.inventoryItem && managePosition.inventoryItem.inventoryItemProducts) {
          for (const iip of managePosition.inventoryItem.inventoryItemProducts) {
            quantity += iip.lotQuantityUnderhood;
          }
        }
      }
    }
    return quantity;
  }

  static getCurrentQuantityInStationByLotNumber(managePosition: ManagePosition, item: InventoryItemProduct): number {
    let quantity = 0;
    if (managePosition) {
      const inventoryItems = this.getInventoryItems(managePosition);
      AbsgArrayHelper.forEach(inventoryItems, ii => {
        AbsgArrayHelper.forEach(ii.inventoryItemProducts, iip => {
          if (item.lotNumber === iip.lotNumber && item.invItemProductIdentifierId === iip.invItemProductIdentifierId) {
            quantity = iip.lotQuantity;
          }
        });
      });
    }
    return quantity;
  }

  static getCurrentQuantityInUnderhoodByLotNumber(managePosition: ManagePosition, item: InventoryItemProduct): number {
    let quantity = 0;
    if (managePosition) {
      const inventoryItems = this.getInventoryItems(managePosition);
      AbsgArrayHelper.forEach(inventoryItems, ii => {
        AbsgArrayHelper.forEach(ii.inventoryItemProducts, iip => {
          if (item.lotNumber === iip.lotNumber && item.invItemProductIdentifierId === iip.invItemProductIdentifierId) {
            quantity = iip.lotQuantityUnderhood;
          }
        });
      });
    }
    return quantity;
  }

  static getCurrentQuantityInStationExcludingLotNumber(arrInventoryItemProductLot: InventoryItemProductLot[],
    item: InventoryItemProductLot): number {
    let quantity = 0;
    for (const iip of arrInventoryItemProductLot) {
      if (!(item.lotNumber === iip.lotNumber && item.invItemProductIdentifierId === iip.invItemProductIdentifierId)) {
        if (iip.isAudited) {
          quantity += iip.unsavedPositionLotQuantity;
        } else {
          quantity += iip.lotQuantity;
        }
      }
    }
    return quantity;
  }

  static getCurrentQuantityInUnderhoodExcludingLotNumber(arrInventoryItemProductLot: InventoryItemProductLot[],
    item: InventoryItemProductLot): number {
    let quantity = 0;
    for (const iip of arrInventoryItemProductLot) {
      if (!(item.lotNumber === iip.lotNumber && item.invItemProductIdentifierId === iip.invItemProductIdentifierId)) {
        if (iip.isAudited) {
          quantity += iip.unsavedUnderHoodLotQuantity;
        } else {
          quantity += iip.lotQuantityUnderhood;
        }
      }
    }
    return quantity;
  }

  static getCurrentQuantityForBinItemInStation(managePosition: ManagePosition): number {
    let quantity = 0;
    if (managePosition) {
      for (const inventoryItem of managePosition.inventoryItems) {
        if (inventoryItem) {
          quantity += AbsgArrayHelper.sumBy(inventoryItem.inventoryItemProducts, (iip) => iip.lotQuantity);
        }
      }
    }
    return quantity;
  }

  static getCurrentQuantityInStationString(managePosition: ManagePosition, defaultValue = '0'): string {
    let currentQuantityString = defaultValue;

    if (managePosition) {
      const qis = this.getCurrentQuantityInStation(managePosition);

      if (qis !== 0) {
        currentQuantityString = CalculationHelper.convertToQuantityString(qis,
          managePosition.productDetail.dFactor,
          managePosition.productDetail.iFactor);
      } else {
        currentQuantityString = defaultValue;
      }
    }

    return currentQuantityString;
  }

  static getTotalPendingQtyWithInvUOMString(managePosition: ManagePosition): string {

    let qohInInvUnits = '';
    let qohInDispenseUnits = 0;
    if (managePosition && managePosition.inventoryItem && managePosition.productDetail) {
      qohInDispenseUnits = managePosition.isBinned() ? ManagePositionHelper.getTotalPendingQtyForBinItem(managePosition)
        : ManagePositionHelper.getTotalPendingQuantity(managePosition.inventoryItem);

      qohInInvUnits = ManagePositionHelper.convertPositionQtyFromDisToInvUnit(qohInDispenseUnits, managePosition);

      if (qohInInvUnits !== '') {
        qohInInvUnits = `${qohInInvUnits} ${managePosition.productDetail.iUomDisplay}`;
      }
    }
    return qohInInvUnits;
  }

  static getTotalPendingQtyForBinItem(managePosition: ManagePosition): number {
    let quantity = 0;
    if (managePosition) {
      quantity = AbsgArrayHelper.sumBy(managePosition.inventoryItems, (inventoryItem) => ManagePositionHelper.getTotalPendingQuantity(inventoryItem));
    }
    return quantity;
  }

  static sortListByPositionNumber(managePositions: ManagePosition[]): ManagePosition[] {
    return managePositions.sort((i1, i2) => {
      const accessPointNameCompareResult = i1.storagePosition.parentContainerName
        .localeCompare(i2.storagePosition.parentContainerName);
      if (accessPointNameCompareResult === 0) {
        return i1.positionNumber < i2.positionNumber ? -1 : 1;
      } else {
        return accessPointNameCompareResult;
      }
    });
  }

  static checkIfLotExists(lotNumber: string, listOfInventoryItemProductLot: InventoryItemProductLot[]): boolean {
    let retVal = false;
    if (!AbsgArrayHelper.isNil(listOfInventoryItemProductLot)) {
      const filterList = listOfInventoryItemProductLot.filter(x => x.lotNumber === lotNumber);
      retVal = filterList.length > 0;
    }
    return retVal;
  }

  static getQuantityInInvUOM(quantityInDispenseUnit: number, dispenseUnitOfMeasureConversionFactor: number,
    inventoryUnitOfMeasureConversionFactor: number): string {
    return dispenseUnitOfMeasureConversionFactor
      ? CalculationHelper.convertToQuantityString(quantityInDispenseUnit,
        dispenseUnitOfMeasureConversionFactor, inventoryUnitOfMeasureConversionFactor, 4, '0')
      : '0';
  }

  // TODO: when all the code is refactored to show display name, and sorting by the trade name
  // becomes unnecessary, remove sortListByTradeName function
  static sortListByTradeName(managePositions: ManagePosition[]): ManagePosition[] {
    return managePositions.sort((i1, i2) =>
      i1.productDetail.tradeName
        .localeCompare(i2.productDetail.tradeName) < 0 ? -1 : 1);
  }

  static sortListByDisplayName(managePositions: ManagePosition[]): ManagePosition[] {
    return managePositions.sort((i1, i2) => i1.productDetail.displayName
      .localeCompare(i2.productDetail.displayName) < 0 ? -1 : 1);
  }

  static sortListByProductDisplayName(managePositions: ManagePosition[]): ManagePosition[] {
    return managePositions.sort((i1, i2) => i1.getProductDisplayName()
      .localeCompare(i2.getProductDisplayName()) < 0 ? -1 : 1);
  }

  static formatDateOnly(date: Date, defaultValue = ''): string {
    const formattedDate = DateTimeHelper.formatDate(date);
    if (!date
      || DateTimeHelper.isMinDate(date)
      || formattedDate === ManagePositionHelper.formattedMarkerDate) {
      return defaultValue;
    } else {
      return formattedDate;
    }
  }

  static isDigit(digit: string): boolean {
    const digits = new RegExp('[0-9]');
    if (!digit || digit.length !== 1) {
      return false;
    }

    return digits.test(digit);
  }

  static getCurrentPositionMaxRule(managePosition: ManagePosition): RuleContainerSetting {
    if (managePosition && managePosition.positionRules) {
      const rules = managePosition.positionRules;
      if (rules && rules.length > 0) {
        for (const rule of rules) {
          if (rule.containerId === managePosition.inventoryItem.positionContainerId
            && rule.rule.ruleId === Rules.positionLevelRules.max) {
            if (rule.value && Number.parseInt(rule.value, 10) >= 0) {
              rule.value = Number.parseInt(rule.value, 10).toString();
            }
            return rule;
          }
        }
      }
    }
  }

  static getCurrentPositionMinRule(managePosition: ManagePosition): RuleContainerSetting {
    if (managePosition && managePosition.positionRules) {
      const rules = managePosition.positionRules;
      if (rules && rules.length > 0) {
        for (const rule of rules) {
          if (rule.containerId === managePosition.inventoryItem.positionContainerId
            && rule.rule.ruleId === Rules.positionLevelRules.min) {
            if (rule.value && Number.parseInt(rule.value, 10) >= 0) {
              rule.value = Number.parseInt(rule.value, 10).toString();
            }
            return rule;
          }
        }
      }
    }
  }

  static getCurrentPositionMaxRuleByStoragePosition(managePosition: ManagePosition): RuleContainerSetting {
    const rule = AbsgArrayHelper.find(managePosition?.positionRules, r => (r.containerId === managePosition.storagePosition.id
      && r.rule.ruleId === Rules.positionLevelRules.max));
    if (rule?.value && Number.parseInt(rule?.value, 10) >= 0) {
      rule.value = Number.parseInt(rule.value, 10).toString();
    }
    return rule;
  }

  static getCurrentPositionMinRuleByStoragePosition(managePosition: ManagePosition): RuleContainerSetting {
    const rule = AbsgArrayHelper.find(managePosition?.positionRules, r => (r.containerId === managePosition.storagePosition.id
      && r.rule.ruleId === Rules.positionLevelRules.min));
    if (rule?.value && Number.parseInt(rule?.value, 10) >= 0) {
      rule.value = Number.parseInt(rule.value, 10).toString();
    }
    return rule;
  }

  static getCurrentPositionPreferredItemIdRule(managePosition: ManagePosition): RuleContainerSetting {
    if (managePosition && managePosition.positionRules) {
      const rules = managePosition.positionRules;
      if (rules && rules.length > 0) {
        for (const rule of rules) {
          if (rule.containerId === managePosition.inventoryItem.positionContainerId
            && rule.rule.ruleId === Rules.positionLevelRules.preferredItemId) {
            return rule;
          }
        }
      }
    }
  }

  static getCurrentPositionDispenseTypeIdRule(managePosition: ManagePosition): RuleContainerSetting {
    if (managePosition && managePosition.positionRules) {
      const rules = managePosition.positionRules;
      if (rules && rules.length > 0) {
        for (const rule of rules) {
          if (rule.containerId === managePosition.inventoryItem.positionContainerId
            && rule.rule.ruleId === Rules.positionLevelRules.dispenseTypeId) {
            if (rule.value && Number.parseInt(rule.value, 10) >= 0) {
              rule.value = Number.parseInt(rule.value, 10).toString();
            }
            return rule;
          }
        }
      }
    }
  }

  static getCurrentPositionBinRule(managePosition: ManagePosition): RuleContainerSetting {
    if (managePosition) {
      const positionContainerId = managePosition.inventoryItem.positionContainerId;
      const ruleId = Rules.positionLevelRules.isBin;
      if (managePosition.positionRules) {
        const rules = managePosition.positionRules;
        if (rules && rules.length > 0) {
          for (const rule of rules) {
            if (rule.containerId === positionContainerId
              && rule.rule.ruleId === ruleId) {
              return rule;
            }
          }
        }
      }
      return RuleContainerSettingGenerator.Generate(positionContainerId, 'false', 'IsBin', ruleId, null, null, StringConstants.boolN);
    }
  }

  static convertPositionQtyFromDisToInvUnit(quantityInDispenseUnit: number, currentPositionValue: ManagePosition): string {
    let quantityInInvUnitString = '0';
    if (AbsgArrayHelper.isNil(quantityInDispenseUnit)) {
      return quantityInInvUnitString;
    }
    if (!AbsgArrayHelper.isNil(currentPositionValue) && !AbsgArrayHelper.isNil(currentPositionValue.productDetail)) {
      const currentPositionProductDetail = currentPositionValue.productDetail;
      if (!AbsgArrayHelper.isNil(currentPositionProductDetail.dFactor) &&
        !AbsgArrayHelper.isNil(currentPositionProductDetail.iFactor)) {
        quantityInInvUnitString = CalculationHelper.convertToQuantityString(quantityInDispenseUnit,
          currentPositionProductDetail.dFactor,
          currentPositionProductDetail.iFactor,
          4,
          '0');
      }
    }
    return quantityInInvUnitString;
  }

  static separateBinItemWithProductDetails(managePosition: ManagePosition, isBin = false, action = ''): ManagePosition[] {
    const managePositions: ManagePosition[] = [];
    AbsgArrayHelper.forEach(managePosition.inventoryItems, (inventoryItem) => {
      const position: ManagePosition = AbsgArrayHelper.cloneDeep(managePosition);
      const product = managePosition.productDetails
        .find(elem => elem.supplierProductInclusionId === inventoryItem.supplierProductInclusionId);
      if (AbsgArrayHelper.isNil(product)) {
        throw new Error(`Not found in Inventory Item Summary, SupplierProductInclusionId - ${inventoryItem.supplierProductInclusionId}`);
      }
      position.isBin = isBin;
      position.actionItem = action === '' ? position.actionItem : action;
      position.productDetails = [product];
      position.productDetail = product;
      position.inventoryItem = inventoryItem;
      position.inventoryItems = [inventoryItem];
      managePositions.push(position);
    });

    return managePositions;
  }

  private static getInventoryItems(managePosition: ManagePosition): InventoryItemSummary[] {
    let iis = [];
    if (managePosition.inventoryItems && managePosition.inventoryItems.length !== 0) {
      iis = managePosition.inventoryItems;
    } else {
      iis.push(managePosition.inventoryItem);
    }
    return iis;
  }

  static getPendingQuantityForForPositionWithInvUOMString(managePosition: ManagePosition, overrideBinLogic = false): string {
    let quantity = 0;
    let qohInInvUnits = '';

    if (managePosition) {
      if (managePosition.isBinned() && !overrideBinLogic) {
        quantity = AbsgArrayHelper.sumBy(managePosition.inventoryItems, (inventoryItem) => inventoryItem.pendingQuantity);
      } else {
        quantity = managePosition.inventoryItem ? managePosition.inventoryItem.pendingQuantity : 0;
      }
      qohInInvUnits = ManagePositionHelper.convertPositionQtyFromDisToInvUnit(quantity, managePosition);

      if (qohInInvUnits !== '') {
        qohInInvUnits = `${qohInInvUnits} ${managePosition.productDetail.iUomDisplay}`;
      }
    }
    return qohInInvUnits;
  }

  static getOverstockPendingQtyForPositionWithInvUOMString(managePosition: ManagePosition, overrideBinLogic = false): string {
    let quantity = 0;
    let qohInInvUnits = '';

    if (managePosition) {
      if (managePosition.isBinned() && !overrideBinLogic) {
        quantity = AbsgArrayHelper.sumBy(managePosition.inventoryItems, (inventoryItem) => inventoryItem.pendingOverstockQuantity);
      } else {
        quantity = managePosition.inventoryItem ? managePosition.inventoryItem.pendingOverstockQuantity : 0;
      }

      qohInInvUnits = ManagePositionHelper.convertPositionQtyFromDisToInvUnit(quantity, managePosition);

      if (qohInInvUnits !== '') {
        qohInInvUnits = `${qohInInvUnits} ${managePosition.productDetail.iUomDisplay}`;
      }
    }
    return qohInInvUnits;
  }

  static getPendingAnnexTransferQtyForPositionWithInvUOMString(managePosition: ManagePosition): string {
    let quantity = 0;
    let qohInInvUnits = '';

    if (managePosition) {
      if (managePosition.isBinned()) {
        quantity = AbsgArrayHelper.sumBy(managePosition.inventoryItems, (inventoryItem) => inventoryItem.pendingAnnexTransferQuantity);
      } else {
        quantity = managePosition.inventoryItem ? managePosition.inventoryItem.pendingAnnexTransferQuantity : 0;
      }

      qohInInvUnits = ManagePositionHelper.convertPositionQtyFromDisToInvUnit(quantity, managePosition);

      if (qohInInvUnits !== '') {
        qohInInvUnits = `${qohInInvUnits} ${managePosition.productDetail.iUomDisplay}`;
      }
    }
    return qohInInvUnits;
  }

  private static getTotalPendingQuantity(invSummary: InventoryItemSummary): number {
    return invSummary.pendingQuantity +
      invSummary.pendingOverstockQuantity +
      invSummary.pendingAnnexTransferQuantity;
  }

  static getCurrentQuantityInStationStringForAllItems(managePosition: ManagePosition, defaultValue = '0'): string {
    let currentQuantityString = defaultValue;

    if (managePosition) {
      const qis = this.getCurrentQuantityInStationAllItems(managePosition);

      if (qis !== 0) {
        currentQuantityString = CalculationHelper.convertToQuantityString(qis,
          managePosition.productDetail.dFactor,
          managePosition.productDetail.iFactor);
      } else {
        currentQuantityString = defaultValue;
      }
    }

    return currentQuantityString;
  }

  static getCurrentQuantityUnderHoodStringForAllItems(managePosition: ManagePosition, defaultValue = '0'): string {
    let currentQuantityString = '';

    if (managePosition) {
      const quantityUnderHood = this.getCurrentQuantityInUnderhoodAllItems(managePosition);

      if (quantityUnderHood !== 0) {
        currentQuantityString = CalculationHelper.convertToQuantityString(quantityUnderHood,
          managePosition.productDetail.dFactor,
          managePosition.productDetail.iFactor);
      } else {
        currentQuantityString = defaultValue;
      }
    }

    return currentQuantityString;
  }

  static getCurrentQuantityInStationAllItems(managePosition: ManagePosition): number {
    let quantity = 0;
    if (managePosition) {
      if (managePosition.inventoryItem && managePosition.inventoryItem.inventoryItemProducts) {
        for (const iip of managePosition.inventoryItem.inventoryItemProducts) {
          quantity += iip.lotQuantity;
        }
      }
    }
    return quantity;
  }

  static getCurrentQuantityInUnderhoodAllItems(managePosition: ManagePosition): number {
    let quantity = 0;
    if (managePosition) {
      if (managePosition.inventoryItem && managePosition.inventoryItem.inventoryItemProducts) {
        for (const iip of managePosition.inventoryItem.inventoryItemProducts) {
          quantity += iip.lotQuantityUnderhood;
        }
      }
    }
    return quantity;
  }

  static createDisplayBinItem(position: ManagePosition): UnassignBin[] {
    if (AbsgArrayHelper.isNil(position)) {
      return [];
    }
    const unassignBinItems = AbsgArrayHelper.map((position.productDetails), (product) => {
      const binItem: UnassignBin = new UnassignBin();
      binItem.itemId = product.itemId;
      binItem.displayJcode = product.jCode;
      binItem.productId = product.productId;
      binItem.dispenseUnitOfMeasureConversionFactor = product.dFactor;
      binItem.inventoryUnitOfMeasureConversionFactor = product.iFactor;
      binItem.displayName = product.displayName;
      binItem.alternateName = product.alternateName;
      binItem.unitNdc = product.unitNdc;
      binItem.packageNDC = product.packageNdc;
      binItem.supplierProductInclusionId = product.supplierProductInclusionId;
      binItem.inventoryUnitOfMeasureDisplay = product.iUomDisplay;

      binItem.isPracticeFavorite = product.isPracticeFavorite;
      binItem.isRefrigerated = product.refrigerated;
      binItem.isDiscontinued = product.isDiscontinued;
      binItem.isItem505b2 = product.isItem505b2;
      binItem.formularyTypeId = product.formularyTypeId;
      binItem.is340B = product.is340B;
      binItem.isBuyIn = product.isBuyIn;
      binItem.isSelected = false;
      binItem.isBin = position.isBin;
      return binItem;
    });
    return this.sortUnassignBinItemArray(unassignBinItems);
  }

  private static sortUnassignBinItemArray(positions: UnassignBin[]): UnassignBin[] {
    // sort by preferred Item , Favorites and order of ndc
    const result = AbsgArrayHelper.sortBy(positions, (position) => [!position.isPreferredNdc, !position.isPracticeFavorite, position.displayName]);
    return result;
  }

  static getSelectedPosition(supplierProductInclusionId: number, items: ManagePosition[]): ManagePosition {
    let itemFound: ManagePosition = null;
    itemFound = AbsgArrayHelper.find((items), (item) => item.productDetail.supplierProductInclusionId === supplierProductInclusionId);
    return itemFound;
  }

  static getPurchaseUnits(drug: AssignedProductDetail): string {
    if (drug?.punitSize) {
      const purchaseUnitOfMeasureSize = drug.punitSize.toString();
      const purchaseUnitOfMeasure = drug.pUomDisplay ? drug.pUomDisplay : '';
      const inventoryUnitOfMeasure = drug.iUomDisplay ? drug.iUomDisplay : '';
      const checkIfUnitMatch = drug.iUomDisplay !== drug.pUomDisplay;
      if (checkIfUnitMatch) {
        return `${purchaseUnitOfMeasureSize} ${inventoryUnitOfMeasure} per ${purchaseUnitOfMeasure}`;
      } else {
        return `${purchaseUnitOfMeasureSize} ${purchaseUnitOfMeasure}`;
      }
    } else {
      return '';
    }
  }

  static applyDefaultSort(managePositions: ManagePosition[]): ManagePosition[] {
    return AbsgArrayHelper.orderBy(managePositions, [
      x => x.getProductDisplayName(),
      x => x.isPatientOwnedStatus(),
      x => x.patientInfo?.lastName,
      x => x.patientInfo?.firstName,
      x => x.patientOwnedItemTypeShortName]);
  }

  static getNearestExpirationDate(managePosition: ManagePosition): string {
    const list = this.getSortedLotAndExpirationList(managePosition);
    let result = '';
    if (list.length > 1) {
      AbsgArrayHelper.each(list, (item) => {
        const isUnknownLot = this.isUnknownLot(item);
        const isNotSpecifiedLot = this.isNotSpecifiedLot(item);
        if (isUnknownLot || isNotSpecifiedLot) {
          return;
        }

        if (!isUnknownLot && !isNotSpecifiedLot) {
          result = this.formatExpirationDate(item.expirationDate);
          return false;
        }
      });
    } else if (list.length === 1) {
      if (!AbsgArrayHelper.isNil(list[0].expirationDate)) {
        result = this.formatExpirationDate(list[0].expirationDate);
      }
    }

    return result;
  }

  static isNotSpecifiedLot(lot: InventoryItemProduct): boolean {
    let retVal = false;
    if (lot) {
      retVal = (AbsgArrayHelper.isNil(lot.lotNumber) || lot.lotNumber === '');
    }
    return retVal;
  }

  static isUnknownLot(lot: InventoryItemProduct): boolean {
    let retVal = false;
    if (lot && !AbsgArrayHelper.isNil(lot.lotNumber)) {
      retVal = (lot.lotNumber.toLowerCase() === StringConstants.unknown.toLowerCase());
    }
    return retVal;
  }

  private static formatExpirationDate(expirationDate: Date): string {
    return ManagePositionHelper.formatDateOnly(expirationDate, StringConstants.notSpecified);
  }

  static getSortedLotAndExpirationList(managePositions: ManagePosition): InventoryItemProductLot[] {
    if (!managePositions) {
      return [];
    }
    const listOfInventoryItemProductLot: Array<InventoryItemProductLot> = [];
    const list = managePositions.inventoryItem.inventoryItemProducts;

    for (const lotItem of list) {
      const underhoodQuantity = lotItem.lotQuantityUnderhood || 0;
      if (!ManagePositionHelper.checkIfLotExists(lotItem.lotNumber, listOfInventoryItemProductLot)
        && (CalculationHelper.add(lotItem.lotQuantity, underhoodQuantity) > 0)) {
        const modifiedItem = new InventoryItemProductLot();
        modifiedItem.expirationDate = lotItem.expirationDate;
        modifiedItem.lotNumber = lotItem.lotNumber;
        modifiedItem.elementIdString = InventoryLotHelper.removeSpaceForId(lotItem.lotNumber);
        modifiedItem.lotQuantityString = ManagePositionHelper.getQuantityInInvUOM(
          CalculationHelper.add(lotItem.lotQuantity, underhoodQuantity),
          managePositions.productDetail.dFactor,
          managePositions.productDetail.iFactor);
        modifiedItem.inventoryUOM = managePositions.productDetail.iUomDisplay;
        listOfInventoryItemProductLot.push(modifiedItem);
      }
    }
    return this.sortList(listOfInventoryItemProductLot);
  }

  // Include when date is null, moving null dates further to the end
  // Ex: [40, 100] will return a negative value, sorting 40 as less than 100
  static sortList(lotItems: InventoryItemProductLot[]): InventoryItemProductLot[] {
    return lotItems.sort((a, b) => this.sortDates(a, b));
  }

  static sortDates(a: InventoryItemProductLot, b: InventoryItemProductLot): number {
    const date1ForCompare = Date.parse(DateTimeHelper.formatDateTime(a.expirationDate, DateFormat.USADateOnly));
    const date2ForCompare = Date.parse(DateTimeHelper.formatDateTime(b.expirationDate, DateFormat.USADateOnly));

    if (isNaN(date1ForCompare) && isNaN(date2ForCompare)) {
      return 1; // arbitrarily
    } else if (isNaN(date1ForCompare)) {
      return 1; // moves date1 further down the list
    } else if (isNaN(date2ForCompare)) {
      return -1; // move date 1 up the list and date 2 down the list
    } else if (date1ForCompare < date2ForCompare) {
      return -1;
    } else if (date1ForCompare > date2ForCompare) {
      return 1;
    } else {
      if (a.lotQuantity > b.lotQuantity) {
        return -1;
      } else if (a.lotQuantity < b.lotQuantity) {
        return 1;
      } else {
        return 0;
      }
    }
  }
}
